import React, {useState} from 'react'
import {Box, Typography} from '@mui/material'
import AppDialog from 'src/components-new/feedback/AppDialog'
import Button from 'src/components-new/primitives/Button'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import DatePickerNew from 'src/components-new/inputs/DatePickerNew'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'

export interface PatientEnrollmentFormValues {
  firstName: string
  lastName: string
  birthDate: string
  files: File[]
}

interface PatientEnrollmentDialogNewProps {
  open: boolean
  onClose: () => void
  onSave: (values: PatientEnrollmentFormValues) => void | Promise<void>
}

interface FormState {
  firstName: string
  lastName: string
  birthDate: string | null
  files: File[]
}

const INITIAL_STATE: FormState = {firstName: '', lastName: '', birthDate: null, files: []}

/**
 * Creation-only dialog — new-style equivalent of
 * src/components/templates/NewPatientEnrollmentDialog/NewPatientEnrollmentDialog.tsx +
 * src/components/templates/EnrollmentPatientForm/EnrollmentPatientForm.tsx. There is no
 * readOnly/detail mode, matching the legacy behavior (the list has no row-detail action).
 */
export default function PatientEnrollmentDialogNew({
  open,
  onClose,
  onSave,
}: PatientEnrollmentDialogNewProps): JSX.Element {
  const [form, setForm] = useState<FormState>(INITIAL_STATE)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({...prev, [key]: value}))
    setErrors(prev => ({...prev, [key]: ''}))
  }

  const handleFilesAdd = (fileList: FileList | null) => {
    if (!fileList) return
    setForm(prev => ({...prev, files: [...prev.files, ...Array.from(fileList)]}))
    setErrors(prev => ({...prev, files: ''}))
  }

  const handleRemoveFile = (index: number) => {
    setForm(prev => ({...prev, files: prev.files.filter((_, i) => i !== index)}))
  }

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {}
    if (!form.lastName.trim()) nextErrors.lastName = 'Campo obbligatorio'
    if (!form.firstName.trim()) nextErrors.firstName = 'Campo obbligatorio'
    if (!form.birthDate) {
      nextErrors.birthDate = 'Campo obbligatorio'
    } else if (new Date(form.birthDate).getTime() > Date.now()) {
      nextErrors.birthDate = 'La data di nascita non può essere nel futuro'
    }
    if (form.files.length === 0) nextErrors.files = 'Campo obbligatorio'
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleClose = () => {
    if (saving) return
    setForm(INITIAL_STATE)
    setErrors({})
    onClose()
  }

  const handleSave = async () => {
    if (!validate() || !form.birthDate) return
    setSaving(true)
    try {
      await onSave({
        firstName: form.firstName,
        lastName: form.lastName,
        birthDate: form.birthDate,
        files: form.files,
      })
      setForm(INITIAL_STATE)
      setErrors({})
    } finally {
      setSaving(false)
    }
  }

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      title="Attivazione paziente"
      maxWidth="md"
      actions={(
        <>
          <Button variant="secondary" onClick={handleClose} disabled={saving}>{'Annulla'}</Button>
          <Button variant="primary" onClick={handleSave} loading={saving}>{'Salva'}</Button>
        </>
      )}
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, pt: 1}}>
        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'}, gap: 2}}>
          <TextFieldNew
            label="Cognome Paziente"
            value={form.lastName}
            onChange={v => setField('lastName', v)}
            required
            error={Boolean(errors.lastName)}
            helperText={errors.lastName}
            fullWidth
          />
          <TextFieldNew
            label="Nome Paziente"
            value={form.firstName}
            onChange={v => setField('firstName', v)}
            required
            error={Boolean(errors.firstName)}
            helperText={errors.firstName}
            fullWidth
          />
          <DatePickerNew
            label="Data di nascita"
            value={form.birthDate}
            onChange={v => setField('birthDate', v)}
            required
            error={Boolean(errors.birthDate)}
            helperText={errors.birthDate}
            fullWidth
          />
        </Box>

        <Box>
          <Typography variant="caption" sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em'}}>
            {'Modulo di adesione *'}
          </Typography>
          <Box
            component="label"
            onDragOver={(e: React.DragEvent) => { e.preventDefault(); e.stopPropagation() }}
            onDrop={(e: React.DragEvent) => {
              e.preventDefault()
              e.stopPropagation()
              handleFilesAdd(e.dataTransfer.files)
            }}
            sx={{
              display: 'block',
              mt: 0.5,
              border: `2px dashed ${errors.files ? 'error.main' : NEUTRAL_SCALE[200]}`,
              borderRadius: '8px',
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: NEUTRAL_SCALE[50],
              '&:hover': {borderColor: NEUTRAL_SCALE[300], backgroundColor: NEUTRAL_SCALE[100]},
            }}
          >
            <input
              type="file"
              multiple
              hidden
              onChange={(e) => handleFilesAdd(e.target.files)}
            />
            <Typography variant="body2" color="text.secondary">
              {'Trascina i file qui o clicca per caricare il modulo di adesione'}
            </Typography>
          </Box>
          {errors.files && (
            <Typography variant="caption" sx={{color: 'error.main'}}>{errors.files}</Typography>
          )}
        </Box>

        {form.files.map((file, index) => (
          <Box
            key={`${file.name}-${index}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1.5,
              border: `1px solid ${NEUTRAL_SCALE[200]}`,
              borderRadius: '8px',
            }}
          >
            <Typography variant="body2">
              {file.name}
              <Typography component="span" variant="caption" sx={{ml: 1, color: 'text.secondary'}}>
                {`(${(file.size / 1024 ** 2).toFixed(2)} MB)`}
              </Typography>
            </Typography>
            <ActionIconButton variant="delete" onClick={() => handleRemoveFile(index)} />
          </Box>
        ))}
      </Box>
    </AppDialog>
  )
}
